import React from 'react';
import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import config from '@config';
import { getUrlsFromPath } from '@utils/i18n';

function getWebsiteSchemaOrgJsonLD(url, title) {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url,
      name: title,
      alternateName: config.title,
    },
  ];
}

const metaNames = {
  TWITTER_TITLE: 'twitter:title',
  TWITTER_DESCRIPTION: 'twitter:description',
  TWITTER_IMAGE: 'twitter:image',
  TWITTER_URL: 'twitter:url',
};

/**
 * Return meta data needed to be customized per page
 * @param {meta} meta object that represents description, image, url, and title.
 * @returns Helmet code that contains the metadata
 */
function getCustomMetadata({ meta, hideAlternateLinks, hideCanonicalLink }) {
  const {
    title,
    description,
    keywords,
    image,
    languageCode,
    pathname,
    robots,
    url: metaUrl,
    metaTags,
  } = meta;
  const {
    url, lang, otherUrl, otherLang,
  } = getUrlsFromPath({ path: pathname, lang: languageCode });

  const doHideAlternateLinks = hideAlternateLinks || (robots && robots.includes('noindex'));
  const customMetaTags = metaTags?.length > 0 && metaTags.map(tag => {
    const isOverwritten = Object.values(metaNames).includes(tag.name);
    return !isOverwritten && <meta name={tag.name} content={tag.content} />;
  });

  return (
    <Helmet>
      {/* General tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="image" content={image} />
      {/* OpenGraph tags */}
      <meta property="og:title" content={title} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={metaUrl} />
      <meta property="og:description" content={description} />
      {/* Twitter tags */}
      <meta name={metaNames.TWITTER_TITLE} content={title} />
      <meta name={metaNames.TWITTER_DESCRIPTION} content={description} />
      <meta name={metaNames.TWITTER_IMAGE} content={image} />
      <meta property={metaNames.TWITTER_URL} content={metaUrl} />
      {!doHideAlternateLinks && otherUrl && otherLang && (
        <link rel="alternate" href={otherUrl} hrefLang={otherLang} />
      )}
      {!doHideAlternateLinks && url && lang && (
        <link rel="alternate" href={url} hrefLang={lang} />
      )}
      {!hideCanonicalLink && url && <link rel="canonical" href={url} />}
      {robots && <meta name="robots" content={robots} />}
      {customMetaTags}
    </Helmet>
  );
}

/**
 * Return both standard meta data and schema.org JSON-LD for a
 * Category page (think programs/course list pages)
 * @param {meta} meta object that represents description, image, url, and title.
 * @returns Helmet code that renders out the metadata and website JsonLD information
 */
function SEOPage({
  meta, hideAlternateLinks, hideCanonicalLink,
}) {
  const customMetadata = getCustomMetadata({ meta, hideAlternateLinks, hideCanonicalLink });
  const { pathname, languageCode, title } = meta;
  const { url } = getUrlsFromPath({
    path: pathname, lang: languageCode,
  });
  const websiteJsonLD = getWebsiteSchemaOrgJsonLD(url, title);
  return (
    <>
      {customMetadata}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(websiteJsonLD)}
        </script>
      </Helmet>
    </>
  );
}

getWebsiteSchemaOrgJsonLD.propTypes = {
  title: PropTypes.string,
  url: PropTypes.string,
};

const commonPropTypes = {
  meta: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    image: PropTypes.string,
    pathname: PropTypes.string,
    robots: PropTypes.string,
    languageCode: PropTypes.oneOf(['en', 'es']),
  }).isRequired,
  hideAlternateLinks: PropTypes.bool,
  hideCanonicalLink: PropTypes.bool,
};

const commonDefaultProps = {
  hideAlternateLinks: false,
  hideCanonicalLink: false,
};

getCustomMetadata.propTypes = commonPropTypes;
getCustomMetadata.defaultProps = commonDefaultProps;
SEOPage.propTypes = commonPropTypes;
SEOPage.defaultProps = commonDefaultProps;

export default SEOPage;
